/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import * as React from 'react';
import { css } from '@emotion/react';
import { connect } from 'react-redux';
import { IKON_MODAL } from '../../core/modal/ikonModal';
import { IAppState } from '../../core/redux/IAppState';
import { defaultMergeProps } from '../../core/redux/sugar';
import { Loader } from '../../forms/elements/loader/Loader';
import ImmutableMap from '../../language/ImmutableMap';
import { isNilOrEmpty, assoc } from '../../language/module';
import { colorForAgeGroup } from '../colorMapping';
import { QuickEntitlement } from '../entitlements/models';
import { getAgeGroup, getPassesWithSeason, hasActivePass } from '../entitlements/module';
import { getHotlistedActiveEntitlementIdsForGuest } from '../hotlist/module';
import GuestCardOverflowMenu, { calcOverflowHeightPx } from './GuestCardOverflowMenu';
import {
  GuestCardDispatchProps,
  GuestCardProps,
  GuestCardState,
  GuestCardStateProps,
  INITIAL_GUEST_CARD_STATE,
  mapDispatchToProps,
  mapStateToProps,
  statesAreEqual,
} from './guestCardProps';
import {
  CARD_PADDING,
  CollapsedOverflowMenuContainer,
  ErrorText,
  ExpandedOverflowMenuContainer,
  GuestCardRoot,
  HotListBanner,
  HotListBannerText,
  OverflowMenuCollapsed,
  OverflowMenuEmptySpace,
  OverflowMenuExpanded,
} from './guestCardStyles';
import GuestProfileView from './GuestProfileView';
import PassOverview from './PassOverview';
import PrimaryActions from './PrimaryActions';
import SecondaryActions from './SecondaryActions';
import { getResource } from '../../language/resources/getResource';
import { GuestPaymentRegistration } from '../guestPayment/paymentRegistration';

const GuestCardError = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
`;

const GuestCardAgeGroup = (color: string) => css`
  height: 8px;
  background-color: ${color};
  transition: background-color 500ms ease-in;
  margin-bottom: 15px;
`

const GuestCardContainer = css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex-grow: 1;
  justify-content: space-around;
  padding: 0 ${CARD_PADDING}px ${2 * CARD_PADDING}px ${CARD_PADDING}px;
`

type Props = GuestCardStateProps & GuestCardDispatchProps & GuestCardProps;

/**
 * GuestCard: a component that renders a single guest and all of their associated actions; e.g. submit waiver, print
 * pass. GuestCard is so big, so central to this app, that large pieces of its functionality have been split out to
 * different files. Things like its prop type declaration and redux functions live elsewhere. It also delegates its
 * render logic to a number of child views.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect<GuestCardStateProps, GuestCardDispatchProps, GuestCardProps, Props, IAppState>(
  mapStateToProps,    // Take App State and use it to make props for this GuestCard component
  mapDispatchToProps, // Take Redux's function that dispatches actions to app state and use it to make action handlers
  defaultMergeProps,
  {
    areStatesEqual: statesAreEqual,
  },
)(class GuestCard extends React.PureComponent<Props, GuestCardState> {
    public state = INITIAL_GUEST_CARD_STATE;


    public openModal = (name: IKON_MODAL, callback?: (state: GuestCardState) => GuestCardState, extras?: object) => {
      this.props.showModal(name, { guestId: this.props.guest!.masterGuestId, ...extras });

      if (callback) {
        this.setState(callback);
      }
    };


    public toggleOverflowMenu = () => this.setState(
      prev => assoc('overflowMenuIsOpen', !prev.overflowMenuIsOpen, prev),
    );


    public callBackToCloseMenu = state => assoc('overflowMenuIsOpen', false, state);
    public onProfileImgClick   = () => this.openModal(IKON_MODAL.GUEST_PHOTO);
    public onPrintPassClick    = () => this.openModal(IKON_MODAL.GUEST_PRINTER);
    public onAssocRfidClick    = () => this.openModal(IKON_MODAL.GUEST_RFID);
    public onWaiverButtonClick = () => this.openModal(IKON_MODAL.GUEST_WAIVER);
    public onDtwButtonClick    = () => this.openModal(IKON_MODAL.GUEST_DTW);
    public onFnfButtonClick    = () => this.openModal(IKON_MODAL.GUEST_FNF);
    public onImportGuestClick  = () => this.props.importGuest!(this.props.guest!);
    public onAddToHotlistClick = () => this.openModal(IKON_MODAL.GUEST_ADD_TO_HOTLIST, this.callBackToCloseMenu);
    public onPaymentRegistrationsClick = (guestPaymentRegistration: GuestPaymentRegistration) => 
      this.openModal(IKON_MODAL.GUEST_PAYMENT_METHOD, this.callBackToCloseMenu, 
      {
        guestPaymentRegistration: guestPaymentRegistration,
      }
    );

    public onRemoveFromHotlistClick = () => this.openModal(
      IKON_MODAL.GUEST_REMOVE_FROM_HOTLIST,
      this.callBackToCloseMenu as any,
    );

    public onMediaHistoryClick = () =>
      this.openModal(
        IKON_MODAL.GUEST_MEDIA_HISTORY,
        this.callBackToCloseMenu as any,
        {
          allowDisableMedia:
            hasActivePass(this.props.passEntitlementId, this.props.guest) && this.props.enableDisableMedia
        });
    public onViewCommentsClick = () => this.openModal(IKON_MODAL.GUEST_COMMENT_HISTORY, this.callBackToCloseMenu);
    public onAddCommentClick   = () => this.openModal(IKON_MODAL.GUEST_ADD_COMMENT, this.callBackToCloseMenu);
    public onViewDaysClick     = () => this.openModal(IKON_MODAL.GUEST_VIEW_DAYS, this.callBackToCloseMenu);


    public onCopyMediaIdClick       = (mediaId: string) => this.props.copyMediaIdToClipboard!(mediaId);
    public onCopyMasterGuestIdClick = () => {
      this.props.copyGuestIdToClipboard!(this.props.guest!.masterGuestId);
      this.toggleOverflowMenu();
    };


    public renderBanner = (deferredMessage, hotListMessage, disabledMessage) => (
      <HotListBanner
        title={deferredMessage || hotListMessage || disabledMessage}
        deferred={!isNilOrEmpty(deferredMessage)}
        disabled={!isNilOrEmpty(disabledMessage)}
      >
        <HotListBannerText>{deferredMessage || hotListMessage || disabledMessage}</HotListBannerText>
      </HotListBanner>
    );

    public renderCardContent = (props: Props, state: GuestCardState) => {
      // Show error if we failed to load the guest's details
      if (props.guestDetailsError) {
        return (
          <div
            css={GuestCardError}
          >
            <ErrorText>{getResource(this.props.resources, 'GUEST_CARD_FAILED_TO_LOAD')}</ErrorText>
          </div>
        );
      }

      // Show loading if we don't have a guest
      if (props.loadingGuestDetails || !props.guest) {
        return <Loader />;
      }


      // Render guest card
      return <React.Fragment>
        { // Age group bar
          !isNilOrEmpty(props.deferredBannerMessage || props.hotlistBannerMessage || props.disabledMediaBannerMessage)
            ? this.renderBanner(props.deferredBannerMessage, props.hotlistBannerMessage, props.disabledMediaBannerMessage)
            : <div
              title={getAgeGroup(props.passEntitlementId, props.guest)}
              css={GuestCardAgeGroup(colorForAgeGroup(getAgeGroup(props.passEntitlementId, props.guest)))}
            />
        }

        <div
          id={`guest-card-${props.guest?.firstName}-${props.guest?.lastName}-${props.guest?.masterGuestId}`}
          css={GuestCardContainer}
        >

          {/* First / Last / Age / Profile Image */}
          <GuestProfileView
            resources={props.resources}
            dateFormat={props.dateFormat}
            guest={props.guest}
            isLoadingPhoto={props.loadingPhoto}
            onProfileImgClick={this.onProfileImgClick}
            copyToClipboard={this.props.copyGuestIdToClipboard}
            showPaymentRegistrations={this.props.showPaymentRegistrations}
            onPaymentRegistrationsClick={this.onPaymentRegistrationsClick}
          />

          {/* Pass Info */}
          <PassOverview
            guest={props.guest}
            passEntitlementId={props.passEntitlementId}
            hotlistedPassIds={getHotlistedActiveEntitlementIdsForGuest(props.guest, props.entitlementHotlists)}
            maximumLength={3}
            copyToClipboard={this.onCopyMediaIdClick}
            rootCss={css`height: 80px;`}
            allEntitlementTypes={this.props.entitlementTypes}
          />

          {/* Print / RFID / Waiver */}
          <PrimaryActions
            guest={props.guest}
            hasHadPrintPdf={props.isPrinted}
            loadingRfid={props.loadingRfid}
            loadingWaiver={props.loadingWaiver}
            passEntitlementId={props.passEntitlementId}
            onPrintPassClick={this.onPrintPassClick}
            onAssocRfidClick={this.onAssocRfidClick}
            onWaiverButtonClick={this.onWaiverButtonClick}
            allEntitlementTypes={props.entitlementTypes || ImmutableMap.empty()}
          />

          {/* Redeem DTW & F&F / Import Guest */}
          <SecondaryActions
            guest={props.guest}
            entitlementHotlists={props.entitlementHotlists}
            loadingImportGuest={props.loadingImportGuest}
            importGuestSuccess={props.importGuestSuccess}
            passEntitlementId={props.passEntitlementId}
            onDtwButtonClick={this.onDtwButtonClick}
            onFnfButtonClick={this.onFnfButtonClick}
            onImportGuestClick={this.onImportGuestClick}
            allEntitlementTypes={props.entitlementTypes || ImmutableMap.empty()}
            enableCustomerImport = {props.enableCustomerImport}
          />
        </div>

        {/* Overflow Menu */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${state.overflowMenuIsOpen ? ExpandedOverflowMenuContainer : CollapsedOverflowMenuContainer};
          `}
        >

          {/* Space-filling div to push overflow menu to bottom of card */}
          <div onClick={this.toggleOverflowMenu} css={OverflowMenuEmptySpace} />

          <GuestCardOverflowMenu
            resources={this.props.resources}
            guest={props.guest}
            enableGlobalHotlist={props.enableGlobalHotlist}
            enableResortSpecificHotlist={props.enableResortSpecificHotlist}
            hasHotlistablePass={props.hasHotlistablePass}
            hasHotlistedPass={props.hasHotlistedPass}
            hasExpiredHotlistedPass={props.hasExpiredHotlistedPass}
            hasHotlistHistory={props.hasHotlistHistory}
            passEntitlementId={props.passEntitlementId}
            onOverflowButtonClick={this.toggleOverflowMenu}
            onViewDaysClick={this.onViewDaysClick}
            onCopyMasterGuestIdClick={this.onCopyMasterGuestIdClick}
            onAddToHotlistClick={this.onAddToHotlistClick}
            onRemoveFromHotlistClick={this.onRemoveFromHotlistClick}
            onMediaHistoryClick={this.onMediaHistoryClick}
            onViewCommentsClick={this.onViewCommentsClick}
            onAddCommentClick={this.onAddCommentClick}
            rootCss={
              state.overflowMenuIsOpen
                ? css`
                  ${OverflowMenuExpanded};
                  /* We always show the "Add to hotlist" menu option now */
                  height: ${calcOverflowHeightPx(true, props.hasHotlistedPass)}px;
                `
                : OverflowMenuCollapsed
            }
          />
        </div>
      </React.Fragment>;
    };

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<GuestCardState>, snapshot?: any) {
      if(prevProps.guest && !prevState.hasLoadedMediaHistory) {
        this.setState(prev => assoc('hasLoadedMediaHistory', true, prev));
        prevProps.getMediaHistory!(prevProps.guest!.masterGuestId);
      }

      const { guest } = this.props;

      if (!guest) {
        return;
      }

      // Download profile photo
      if (
        guest !== prevProps.guest &&
        !this.props.loadingPhoto &&
        guest.photo?.smallPhotoUrl &&
        !guest.photo.imgSmall
      ) {
        this.props.getPhoto!(guest, guest.photo.smallPhotoUrl);
      }

      // Download entitlements
      if (
        guest !== prevProps.guest &&
        R.isNil(guest.entitlements) &&
        !this.props.loadingEntitlements
      ) {
        this.props.getEntitlements!(guest);
      }

      // Get hotlist information for entitlements
      if (
        guest.entitlements !== prevProps.guest?.entitlements &&
        !R.isEmpty(guest.entitlements) &&
        !this.props.loadingEntitlementHotlists &&
        this.props.getEntitlementHotlists &&
        !R.isNil(this.props.entitlementHotlists)
      ) {
        R.forEach(
          (entitlement: QuickEntitlement) => {
            if (!this.props.entitlementHotlists!.has(`${entitlement.entitlementId}`)) {
              this.props.getEntitlementHotlists!(
                entitlement,
                this.props.resortList
              );
            }
          },
          getPassesWithSeason(
            this.props.passEntitlementId,
            guest,
            this.props.entitlementTypes || ImmutableMap.empty()
          ) || [],
        );
      }

      // Download hasComments status
      if (
        guest.masterGuestId !== prevProps.guest?.masterGuestId &&
        R.isNil(guest.hasComments) &&
        !this.props.loadingHasComments
      ) {
        this.props.getHasComments!(guest.masterGuestId);
      }

      // Download payment registrations
      if (
        this.props.showPaymentRegistrations &&
        (!guest.paymentRegistrations || R.isNil(guest.paymentRegistrations)) &&
        !this.props.loadingPaymentRegistrations
      ) {
        this.props.getPaymentRegistrations!(guest);
      }

      if (
        this.props.showPaymentRegistrations &&
        guest.paymentRegistrations &&
        guest.paymentRegistrations.length > 0 &&
        (!guest.paymentMethod || R.isNil(guest.paymentMethod)) &&
        !this.props.loadingPaymentMethod
      ) {
        this.props.getPaymentMethod(guest);
      }
    }


    public render = () => (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: stretch;
          flex-grow: 1;
          ${GuestCardRoot};
          ${this.props.rootCss};
        `}
      >{this.renderCardContent(this.props, this.state)}</div>
    );
  },
);
